import {
    Box,
    Typography,
    Stack,
    StackProps,
    debounce,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useState } from "react";

import WidgetWraper from "components/Common/WidgetWraper";
import LoaderComponent from "components/Loader";
import { InfoTooltip } from "components/Tooltip/InfoTooltip";

const RENEWAL_TIMELINE_CATEGORIES = ["0-30 Days", "31-60 Days", "61-90 Days"];

const RENEWAL_TIMELINE_BAR_COLORS = ["#FFA200","#00BF7D","#00B4C5"]

export type RenewalTimelineData = {
    days0To30: number;
    days31To60: number;
    days61To90: number;
};

export interface RenewalTimelineWidgetProps {
    title?: string;
    data: RenewalTimelineData;
    isLoading?: boolean;
    hasTooltip?: boolean;
    tooltipLabel?: React.ReactNode;
    containerProps?: StackProps;
}

function buildRenewalTimelineOptions(
    data: RenewalTimelineData,
    labelColor: string,
    chartBackground: string
) {
    const values = [data.days0To30, data.days31To60, data.days61To90];

    return {
        backgroundColor: chartBackground,
        textStyle: { color: labelColor },
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
        },
        grid: {
            left: "8%",
            right: "5%",
            bottom: "15%",
            top: "5%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: RENEWAL_TIMELINE_CATEGORIES,
            axisLabel: {
                color: labelColor,
                fontSize: 12,
            },
            axisTick: { show: false },
            axisLine: { show: true, lineStyle: { color: "#E0E0E0" } },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                color: labelColor,
                fontSize: 12,
            },
            axisTick: { show: false },
            axisLine: { show: false },
            splitLine: {
                show: true,
                lineStyle: {
                    type: "dotted",
                    color: "#E0E0E0",
                    width: 1,
                },
            },
        },
        series: [
            {
                type: "bar",
                data: values,
                barWidth: "50%",
                itemStyle: {
                    color: (params: any) => RENEWAL_TIMELINE_BAR_COLORS[params.dataIndex],
                },
            },
        ],
    };
}

export default function RenewalTimelineWidget({
    title = "Renewal Timeline",
    data,
    isLoading = false,
    hasTooltip = true,
    tooltipLabel,
    containerProps,
}: Readonly<RenewalTimelineWidgetProps>) {
    const theme = useTheme();
    const colors = theme.palette;
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    const chartOptions = useMemo(
        () =>
            buildRenewalTimelineOptions(
                data,
                colors.widget?.chart?.labelColorSecondary ?? "#000000",
                colors.widget?.background?.chart ?? "#ffffff"
            ),
        [data, colors.widget?.chart?.labelColorSecondary, colors.widget?.background?.chart]
    );

    useEffect(() => {
        const handleResize = debounce(() => setWindowSize(window.innerWidth), 200);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);



    return (
        <WidgetWraper>
            <Box
                sx={{ backgroundColor: colors.widget?.background?.card ?? "#ffffff" }}
                {...containerProps}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                >
                    <Typography
                        variant="widgetTitleNew"
                        sx={{ color: colors.widget?.text?.title ?? "#000000" }}
                    >
                        {title}
                    </Typography>

                    {hasTooltip && tooltipLabel != null && (
                        <InfoTooltip
                            label={
                                typeof tooltipLabel === "string" ? (
                                    <Typography
                                        variant="body2"
                                        sx={{ m: 0, background: "#ffffff", color: "#000000", fontWeight: 500 }}
                                    >
                                        {tooltipLabel}
                                    </Typography>
                                ) : (
                                    tooltipLabel
                                )
                            }
                            cursor="pointer"
                            icon={
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: 22,
                                        color: colors.textLight?.secondary ?? "#0784B5",
                                    }}
                                />
                            }
                        />
                    )}
                </Stack>

                <Box sx={{ width: "100%", height: 280 }} key={windowSize}>
                    {isLoading ? (
                        <LoaderComponent pt={20} />
                    ) : (
                        <ReactECharts
                            option={chartOptions}
                            style={{ width: "100%", height: "100%" }}
                        />
                    )}
                </Box>
            </Box>
        </WidgetWraper>
    );
}